import { useEffect, useState } from "react";
import { Check, X, ChevronRight, Copy, Eye, ArrowRight, Volume2, VolumeX } from "lucide-react";
import useLabSounds from "../hooks/useLabSounds";

export default function GradingModal({ open, result, lab, onClose, onRetry, onNextLab }) {
  const sounds = useLabSounds();
  const [displayScore, setDisplayScore] = useState(0);
  // Track which "needs fix" items have had their answer revealed. Resets on
  // every modal open so revealing a fix doesn't carry across attempts.
  const [revealed, setRevealed] = useState(() => new Set());

  useEffect(() => {
    if (!open || !result) return;
    setRevealed(new Set());
    setDisplayScore(0);
    const target = result.score;
    const start = performance.now();
    const dur = 900;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayScore(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, result]);

  if (!open || !result) return null;
  const passed = result.results.filter((r) => r.passed);
  const failed = result.results.filter((r) => !r.passed);
  const taskResults = result.task_results || [];
  const fb = result.feedback || {};
  const isPerfect = result.score === 100;

  const reveal = (id) =>
    setRevealed((cur) => {
      const next = new Set(cur);
      next.add(id);
      return next;
    });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-xl bg-black/80"
      data-testid="grading-modal"
    >
      <div className="w-full max-w-3xl bg-[#141416] border border-zinc-800 shadow-[0_0_60px_rgba(0,229,255,0.15)] max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
              Grading · {lab?.topic}
            </div>
            <h2 className="text-lg font-display font-bold tracking-tight mt-0.5">{lab?.title}</h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={sounds.toggleMuted}
              title={sounds.muted ? "Unmute lab sounds" : "Mute lab sounds"}
              className="text-zinc-500 hover:text-zinc-100 p-1"
              data-testid="grading-mute-btn"
              aria-pressed={sounds.muted}
            >
              {sounds.muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <button
              onClick={onClose}
              className="text-zinc-500 hover:text-zinc-100 text-xs font-mono uppercase tracking-widest"
              data-testid="grading-close"
            >
              close [ESC]
            </button>
          </div>
        </div>

        <div className="p-6 grid md:grid-cols-[200px_1fr] gap-6 overflow-y-auto">
          {/* Score */}
          <div className="flex flex-col items-center gap-2">
            <div
              className={`font-term text-7xl font-bold ${
                isPerfect ? "text-[#00FF41] text-glow-green" : displayScore >= 60 ? "text-[#00E5FF]" : "text-[#F59E0B]"
              }`}
              data-testid="grading-score"
            >
              {displayScore}
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">/ 100</div>
            <div
              className={`mt-2 px-3 py-1 text-[10px] uppercase tracking-widest font-mono border ${
                isPerfect
                  ? "border-[#00FF41] text-[#00FF41]"
                  : displayScore >= 60
                  ? "border-[#00E5FF] text-[#00E5FF]"
                  : "border-[#F59E0B] text-[#F59E0B]"
              }`}
            >
              {isPerfect ? "PASSED" : displayScore >= 60 ? "PARTIAL" : "KEEP GOING"}
            </div>
          </div>

          <div className="space-y-4">
            {fb.overall_feedback && (
              <div className="text-sm text-zinc-300 leading-relaxed" data-testid="grading-feedback-overall">
                {fb.overall_feedback}
              </div>
            )}

            {taskResults.length > 0 && (
              <div data-testid="grading-tasks">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#00E5FF] font-mono mb-2">
                  tasks ({taskResults.filter((t) => t.passed).length}/{taskResults.length})
                </div>
                <ol className="space-y-2">
                  {taskResults.map((t, i) => {
                    const Icon = t.passed ? Check : X;
                    const color = t.passed
                      ? "text-[#00FF41] border-[#00FF41]/30"
                      : t.blocked_by
                      ? "text-zinc-500 border-zinc-700"
                      : "text-[#EF4444] border-[#EF4444]/30";
                    return (
                      <li
                        key={t.task_id}
                        className={`bg-black border-l-2 ${color} pl-3 py-2 pr-3 text-sm`}
                        data-testid={`grading-task-${i}`}
                      >
                        <div className="flex gap-2 items-start">
                          <Icon size={14} className="shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="text-zinc-100 font-medium">
                              <span className="text-[10px] font-mono text-zinc-500 mr-2">
                                {String(i + 1).padStart(2, "0")}
                              </span>
                              {t.description}
                            </div>
                            {t.rationale && (
                              <div className="mt-1 text-[12px] text-zinc-500 italic">
                                {t.rationale}
                              </div>
                            )}
                            {t.blocked_by && (
                              <div className="mt-1 text-[11px] text-zinc-600 font-mono">
                                blocked by → {t.blocked_by}
                              </div>
                            )}
                            {(t.verify_results || []).concat(t.confirm_results || []).filter(
                              (r) => !r.passed
                            ).length > 0 && (
                              <ul className="mt-1.5 space-y-0.5 text-[12px] text-zinc-400">
                                {(t.verify_results || []).concat(t.confirm_results || []).map(
                                  (r, j) =>
                                    r.passed ? null : (
                                      <li key={j} className="font-mono">
                                        · {r.details}
                                      </li>
                                    )
                                )}
                              </ul>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {passed.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#00FF41] font-mono mb-2">
                  ✓ correct ({passed.length})
                </div>
                <ul className="space-y-1.5">
                  {passed.map((r, i) => (
                    <li key={r.id} className="flex gap-2 text-sm text-zinc-300" data-testid={`grade-pass-${i}`}>
                      <Check size={14} className="text-[#00FF41] shrink-0 mt-0.5" />
                      <span>{r.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {failed.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#EF4444] font-mono mb-2">
                  ✗ needs fix ({failed.length})
                </div>
                <ul className="space-y-3">
                  {failed.map((r, i) => {
                    const fix = (fb.incorrect_items || []).find(
                      (x) => x.item?.toLowerCase().includes(r.description.toLowerCase().slice(0, 10))
                    )?.fix_command;
                    const isRevealed = revealed.has(r.id);
                    return (
                      <li key={r.id} className="bg-black border border-zinc-800 p-3" data-testid={`grade-fail-${i}`}>
                        <div className="flex gap-2 text-sm text-zinc-200">
                          <X size={14} className="text-[#EF4444] shrink-0 mt-0.5" />
                          <span>{r.description}</span>
                        </div>
                        {fix && !isRevealed && (
                          <button
                            type="button"
                            onClick={() => reveal(r.id)}
                            className="mt-3 text-[11px] uppercase tracking-widest font-mono text-zinc-500 hover:text-[#00E5FF] flex items-center gap-1.5 transition-colors"
                            data-testid={`grade-reveal-${i}`}
                          >
                            <Eye size={11} /> show me the fix
                          </button>
                        )}
                        {fix && isRevealed && (
                          <div className="mt-2" data-testid={`grade-fix-${i}`}>
                            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
                              <span>fix commands</span>
                              <button
                                onClick={() => navigator.clipboard?.writeText(fix)}
                                className="hover:text-zinc-200 flex items-center gap-1"
                              >
                                <Copy size={10} /> copy
                              </button>
                            </div>
                            <pre className="font-term text-xs text-[#4ADE80] whitespace-pre-wrap bg-[#0A0A0A] border border-zinc-900 p-2">
                              {fix}
                            </pre>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {fb.next_steps && (
              <div className="border-l-2 border-[#00E5FF] pl-3 text-sm text-zinc-400" data-testid="grading-next-steps">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono block mb-1">
                  next
                </span>
                {fb.next_steps}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-zinc-800 flex items-center justify-end gap-3">
          {isPerfect ? (
            <button
              onClick={() => (onNextLab ? onNextLab() : onClose())}
              className="px-5 py-2.5 text-sm bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC] transition-colors flex items-center gap-2"
              data-testid="grading-next-lab"
            >
              {!lab?.next_lab_id
                ? "back to catalog"
                : lab.next_lab_status === "completed"
                ? "review lab"
                : "next lab"}{" "}
              <ArrowRight size={14} />
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 transition-colors"
                data-testid="grading-keep-working"
              >
                keep working
              </button>
              <button
                onClick={onRetry}
                className="px-4 py-2 text-sm bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC] transition-colors flex items-center gap-2"
                data-testid="grading-retry"
              >
                reset & retry
                <ChevronRight size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
